export const IMAGE_UPLOAD_MAX_PIXELS = 4_000_000;
export const SVG_MAX_PIXELS = 2_000_000;

export const OBJECT_FIT = {
  COVER: "cover",
  CONTAIN: "contain",
};

export const OBJECT_POSITION = {
  CENTER: "center",
  LEFT: "left",
  RIGHT: "right",
  TOP: "top",
  BOTTOM: "bottom",
};

export const imageBlobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const loadImageBlob = (imageBlob) =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(imageBlob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to decode image"));
    };
    try {
      img.src = url;
    } catch (error) {
      URL.revokeObjectURL(url);
      reject(error);
    }
  });

const validateDimensions = (width, height) => {
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    throw new Error("Image dimensions must be finite and positive");
  }
};

const limitDimensions = (width, height, maxPixels) => {
  validateDimensions(width, height);
  if (!Number.isFinite(maxPixels) || maxPixels < 1) {
    throw new Error("Image pixel limit must be at least 1");
  }
  const scale = Math.min(1, Math.sqrt(maxPixels / (width * height)));
  let targetWidth = Math.max(1, Math.floor(width * scale));
  let targetHeight = Math.max(1, Math.floor(height * scale));

  // Clamping a very narrow side to one pixel can put the area over the limit.
  if (targetWidth * targetHeight > maxPixels) {
    if (targetWidth >= targetHeight) {
      targetWidth = Math.floor(maxPixels / targetHeight);
    } else {
      targetHeight = Math.floor(maxPixels / targetWidth);
    }
  }

  return { width: targetWidth, height: targetHeight };
};

const canvasToBlob = (canvas, type) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("Failed to encode image"));
      }
    }, type);
  });

const createCanvas = (width, height) => {
  validateDimensions(width, height);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Image canvas context is unavailable");
  }
  return { canvas, ctx };
};

export const getImageBlobDimensions = async (imageBlob) => {
  const img = await loadImageBlob(imageBlob);
  validateDimensions(img.width, img.height);
  return { width: img.width, height: img.height };
};

export const resizeImageBlob = async (
  imageBlob,
  dimensions,
  maxPixels = IMAGE_UPLOAD_MAX_PIXELS,
) => {
  const { width, height } = limitDimensions(
    dimensions.width,
    dimensions.height,
    maxPixels,
  );
  if (width === dimensions.width && height === dimensions.height) {
    return imageBlob;
  }
  const img = await loadImageBlob(imageBlob);
  const { canvas, ctx } = createCanvas(width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return canvasToBlob(canvas, imageBlob.type);
};

export const convertImageBlobToType = async (
  imageBlob,
  targetType = "jpeg",
  upscaleSvgFactor = 4,
) => {
  const img = await loadImageBlob(imageBlob);
  let width = img.width;
  let height = img.height;
  if (imageBlob.type === "image/svg+xml") {
    const factor = Math.max(1, upscaleSvgFactor);
    ({ width, height } = limitDimensions(
      width * factor,
      height * factor,
      SVG_MAX_PIXELS,
    ));
  }
  const { canvas, ctx } = createCanvas(width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return canvasToBlob(canvas, `image/${targetType}`);
};

export const cropImageBlob = async (
  imageBlob,
  containerSize,
  objectFit = OBJECT_FIT.COVER,
  objectPosition = OBJECT_POSITION.CENTER,
) => {
  const img = await loadImageBlob(imageBlob);
  const { canvas, ctx } = createCanvas(
    containerSize.width,
    containerSize.height,
  );
  const containerRatio = containerSize.width / containerSize.height;
  const imgRatio = img.width / img.height;

  let drawWidth = containerSize.width;
  let drawHeight = containerSize.height;
  let offsetX = 0;
  let offsetY = 0;

  if (objectFit === OBJECT_FIT.COVER) {
    if (containerRatio > imgRatio) {
      drawHeight = containerSize.width / imgRatio;
      offsetY = (containerSize.height - drawHeight) / 2;
    } else {
      drawWidth = containerSize.height * imgRatio;
      offsetX = (containerSize.width - drawWidth) / 2;
    }
  } else if (objectFit === OBJECT_FIT.CONTAIN) {
    if (containerRatio > imgRatio) {
      drawWidth = containerSize.height * imgRatio;
      offsetX = (containerSize.width - drawWidth) / 2;
    } else {
      drawHeight = containerSize.width / imgRatio;
      offsetY = (containerSize.height - drawHeight) / 2;
    }
  }

  if (objectPosition === OBJECT_POSITION.LEFT) {
    offsetX = 0;
  } else if (objectPosition === OBJECT_POSITION.RIGHT) {
    offsetX = containerSize.width - drawWidth;
  } else if (objectPosition === OBJECT_POSITION.TOP) {
    offsetY = 0;
  } else if (objectPosition === OBJECT_POSITION.BOTTOM) {
    offsetY = containerSize.height - drawHeight;
  }

  ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
  return canvasToBlob(canvas, imageBlob?.type ?? "image/jpeg");
};

export const cropImageBlobToBbox = async (imageBlob, { bbox } = {}) => {
  const img = await loadImageBlob(imageBlob);
  const { canvas, ctx } = createCanvas(bbox.w, bbox.h);
  ctx.drawImage(img, bbox.x, bbox.y, bbox.w, bbox.h, 0, 0, bbox.w, bbox.h);
  return canvasToBlob(canvas, imageBlob?.type ?? "image/jpeg");
};

export const createImageColorBlob = async (
  color,
  width = 400,
  height = 400,
) => {
  const { canvas, ctx } = createCanvas(width, height);
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, width, height);
  return canvasToBlob(canvas, "image/png");
};
